import { useState, useEffect } from 'react';
import API from '../utils/API';

const toDateInput = (iso) => (!iso ? '' : iso.slice(0, 10));

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const BLANK_ROUND = {
  round_name: '',
  year: new Date().getFullYear(),
  nominations_enabled: false,
  voting_enabled: false,
  nominations_start: '',
  nominations_end: '',
  voting_start: '',
  voting_end: '',
};

const BLANK_CATEGORY = {
  category_key: '',
  label: '',
  description: '',
  min_years: '',
  max_years: '',
  sort_order: 0,
  active: true,
};

export default function AdminAwards() {
  const [view, setView] = useState('rounds');

  // Rounds
  const [rounds, setRounds] = useState([]);
  const [roundsLoading, setRoundsLoading] = useState(true);
  const [expandedRound, setExpandedRound] = useState(null);
  const [roundCategories, setRoundCategories] = useState({});
  const [catsLoading, setCatsLoading] = useState({});
  const [roundForm, setRoundForm] = useState(null);
  const [catForm, setCatForm] = useState(null);
  const [roundSaving, setRoundSaving] = useState(false);
  const [catSaving, setCatSaving] = useState(false);
  const [roundMsg, setRoundMsg] = useState(null);

  // Nominations
  const [nominations, setNominations] = useState([]);
  const [nomLoading, setNomLoading] = useState(false);
  const [nomFilter, setNomFilter] = useState({ status: '', category: '', round_id: '' });
  const [nomCategories, setNomCategories] = useState([]);

  // Standings
  const [votes, setVotes] = useState([]);
  const [votesLoading, setVotesLoading] = useState(false);
  const [standingsRoundId, setStandingsRoundId] = useState('');
  const [standingsCategories, setStandingsCategories] = useState([]);
  const [standingsNoms, setStandingsNoms] = useState([]);

  const loadRounds = async () => {
    setRoundsLoading(true);
    try {
      const res = await API.adminGetRounds();
      setRounds(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setRoundsLoading(false);
    }
  };

  useEffect(() => {
    loadRounds();
  }, []);

  // Auto-select active round when switching to nominations/standings
  useEffect(() => {
    const active = rounds.find((r) => r.active);
    if (!active) return;
    if (view === 'nominations' && !nomFilter.round_id) {
      setNomFilter((f) => ({ ...f, round_id: String(active.round_id) }));
    }
    if (view === 'standings' && !standingsRoundId) {
      setStandingsRoundId(String(active.round_id));
    }
  }, [view, rounds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load nominations when view=nominations and filter changes
  useEffect(() => {
    if (view !== 'nominations') return;
    setNomLoading(true);
    const params = {};
    if (nomFilter.status) params.status = nomFilter.status;
    if (nomFilter.category) params.category = nomFilter.category;
    if (nomFilter.round_id) params.round_id = nomFilter.round_id;
    API.adminGetNominations(params)
      .then((res) => setNominations(res.data))
      .catch((e) => console.error(e))
      .finally(() => setNomLoading(false));
  }, [view, nomFilter]);

  // Load categories for nomination round filter
  useEffect(() => {
    if (!nomFilter.round_id) { setNomCategories([]); return; }
    API.adminGetRoundCategories(nomFilter.round_id)
      .then((res) => setNomCategories(res.data))
      .catch((e) => console.error(e));
  }, [nomFilter.round_id]);

  // Load standings data when round selected
  useEffect(() => {
    if (view !== 'standings' || !standingsRoundId) return;
    setVotesLoading(true);
    Promise.all([
      API.adminGetAwardVotes({ round_id: standingsRoundId }),
      API.adminGetRoundCategories(standingsRoundId),
      API.adminGetNominations({ round_id: standingsRoundId, status: 'accepted' }),
    ])
      .then(([votesRes, catsRes, nomsRes]) => {
        setVotes(votesRes.data);
        setStandingsCategories(catsRes.data);
        setStandingsNoms(nomsRes.data);
      })
      .catch((e) => console.error(e))
      .finally(() => setVotesLoading(false));
  }, [view, standingsRoundId]);

  // Toggle category expansion for a round
  const toggleExpandRound = async (roundId) => {
    if (expandedRound === roundId) { setExpandedRound(null); return; }
    setExpandedRound(roundId);
    if (!roundCategories[roundId]) {
      setCatsLoading((prev) => ({ ...prev, [roundId]: true }));
      try {
        const res = await API.adminGetRoundCategories(roundId);
        setRoundCategories((prev) => ({ ...prev, [roundId]: res.data }));
      } catch (e) {
        console.error(e);
      } finally {
        setCatsLoading((prev) => ({ ...prev, [roundId]: false }));
      }
    }
  };

  // Round CRUD
  const openNewRound = () => {
    setRoundForm({ mode: 'new', data: { ...BLANK_ROUND } });
    setRoundMsg(null);
  };

  const openEditRound = (round) => {
    setRoundForm({
      mode: 'edit',
      roundId: round.round_id,
      data: {
        round_name: round.round_name,
        year: round.year,
        nominations_enabled: !!round.nominations_enabled,
        voting_enabled: !!round.voting_enabled,
        nominations_start: toDateInput(round.nominations_start),
        nominations_end: toDateInput(round.nominations_end),
        voting_start: toDateInput(round.voting_start),
        voting_end: toDateInput(round.voting_end),
      },
    });
    setRoundMsg(null);
  };

  const saveRound = async () => {
    setRoundSaving(true);
    setRoundMsg(null);
    const mode = roundForm.mode;
    try {
      if (mode === 'new') {
        await API.adminCreateRound(roundForm.data);
      } else {
        await API.adminUpdateRound(roundForm.roundId, roundForm.data);
      }
      setRoundForm(null);
      await loadRounds();
      setRoundMsg({ ok: true, text: mode === 'new' ? 'Round created.' : 'Round updated.' });
    } catch {
      setRoundMsg({ ok: false, text: 'Failed to save round.' });
    } finally {
      setRoundSaving(false);
    }
  };

  const toggleRoundFlag = async (round, field) => {
    try {
      await API.adminUpdateRound(round.round_id, {
        round_name: round.round_name,
        year: round.year,
        nominations_enabled: round.nominations_enabled,
        voting_enabled: round.voting_enabled,
        nominations_start: toDateInput(round.nominations_start),
        nominations_end: toDateInput(round.nominations_end),
        voting_start: toDateInput(round.voting_start),
        voting_end: toDateInput(round.voting_end),
        [field]: !round[field],
      });
      await loadRounds();
    } catch (e) {
      console.error(e);
    }
  };

  const activateRound = async (roundId) => {
    try {
      await API.adminActivateRound(roundId);
      await loadRounds();
    } catch (e) {
      console.error(e);
    }
  };

  const deleteRound = async (roundId) => {
    if (!window.confirm('Delete this round? All associated categories will also be deleted.')) return;
    try {
      await API.adminDeleteRound(roundId);
      await loadRounds();
    } catch (e) {
      console.error(e);
    }
  };

  // Category CRUD
  const openNewCategory = (roundId) => {
    const existing = roundCategories[roundId] || [];
    setCatForm({ roundId, mode: 'new', data: { ...BLANK_CATEGORY, sort_order: existing.length } });
  };

  const openEditCategory = (roundId, cat) => {
    setCatForm({
      roundId,
      mode: 'edit',
      categoryId: cat.category_id,
      data: {
        category_key: cat.category_key,
        label: cat.label,
        description: cat.description || '',
        min_years: cat.min_years ?? '',
        max_years: cat.max_years ?? '',
        sort_order: cat.sort_order,
        active: !!cat.active,
      },
    });
  };

  const saveCategory = async () => {
    setCatSaving(true);
    try {
      if (catForm.mode === 'new') {
        await API.adminCreateCategory(catForm.roundId, catForm.data);
      } else {
        await API.adminUpdateCategory(catForm.categoryId, catForm.data);
      }
      const res = await API.adminGetRoundCategories(catForm.roundId);
      setRoundCategories((prev) => ({ ...prev, [catForm.roundId]: res.data }));
      setCatForm(null);
    } catch (e) {
      console.error(e);
    } finally {
      setCatSaving(false);
    }
  };

  const handleCloneCategories = async (targetRoundId, sourceRoundId) => {
    if (!window.confirm('Copy all categories from the selected round?')) return;
    setCatForm(null);
    setCatsLoading((prev) => ({ ...prev, [targetRoundId]: true }));
    try {
      const res = await API.adminGetRoundCategories(sourceRoundId);
      const sourceCats = res.data;
      
      for (const cat of sourceCats) {
        await API.adminCreateCategory(targetRoundId, {
          category_key: cat.category_key,
          label: cat.label,
          description: cat.description || '',
          min_years: cat.min_years ?? '',
          max_years: cat.max_years ?? '',
          sort_order: cat.sort_order,
          active: !!cat.active,
        });
      }
      const refreshRes = await API.adminGetRoundCategories(targetRoundId);
      setRoundCategories((prev) => ({ ...prev, [targetRoundId]: refreshRes.data }));
    } catch (e) {
      console.error(e);
      alert('Failed to copy some categories. Check system logs.');
    } finally {
      setCatsLoading((prev) => ({ ...prev, [targetRoundId]: false }));
    }
  };

  const deleteCategory = async (roundId, categoryId) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await API.adminDeleteCategory(categoryId);
      const res = await API.adminGetRoundCategories(roundId);
      setRoundCategories((prev) => ({ ...prev, [roundId]: res.data }));
    } catch (e) {
      console.error(e);
    }
  };

  // Build standings from votes + accepted nominations
  const buildStandings = () => {
    const nameMap = {};
    for (const n of standingsNoms) {
      nameMap[`${n.nominee_email}:${n.category}`] = n.nominee_name;
    }
    const result = {};
    for (const v of votes) {
      if (!result[v.category]) result[v.category] = [];
      result[v.category].push({
        email: v.nominee_email,
        name: nameMap[`${v.nominee_email}:${v.category}`] || v.nominee_email,
        votes: parseInt(v.vote_count, 10),
      });
    }
    for (const n of standingsNoms) {
      if (!result[n.category]) result[n.category] = [];
      if (!result[n.category].some((e) => e.email === n.nominee_email)) {
        result[n.category].push({ email: n.nominee_email, name: n.nominee_name, votes: 0 });
      }
    }
    for (const cat of Object.keys(result)) result[cat].sort((a, b) => b.votes - a.votes);
    return result;
  };

  const setRoundField = (field, value) =>
    setRoundForm((f) => ({ ...f, data: { ...f.data, [field]: value } }));

  const setCatField = (field, value) =>
    setCatForm((f) => ({ ...f, data: { ...f.data, [field]: value } }));

  // ── Round form ─────────────────────────────────────────────
  const renderRoundForm = () => {
    if (!roundForm) return null;
    const { data } = roundForm;
    return (
      <div className="awards-round-form">
        <div className="awards-round-form-title">
          {roundForm.mode === 'new' ? 'New Round' : 'Edit Round'}
        </div>
        <div className="awards-form-name-row">
          <div className="awards-form-group">
            <label className="awards-form-label">Round Name</label>
            <input
              className="awards-form-input"
              type="text"
              value={data.round_name}
              onChange={(e) => setRoundField('round_name', e.target.value)}
              placeholder="e.g. 2025 Annual Awards"
            />
          </div>
          <div className="awards-form-group awards-form-group--year">
            <label className="awards-form-label">Year</label>
            <input
              className="awards-form-input"
              type="number"
              value={data.year}
              onChange={(e) => setRoundField('year', parseInt(e.target.value) || data.year)}
              min="2020"
              max="2100"
            />
          </div>
        </div>
        <div className="awards-settings-grid">
          <div className="awards-settings-col">
            <div className="awards-col-head">
              <span className="awards-col-label">Nominations</span>
              <button
                className={`admin-toggle ${data.nominations_enabled ? 'on' : 'off'}`}
                onClick={() => setRoundField('nominations_enabled', !data.nominations_enabled)}
              >
                <i className={`bi ${data.nominations_enabled ? 'bi-toggle-on' : 'bi-toggle-off'}`} />
              </button>
            </div>
            <div className="awards-date-row">
              <label className="awards-date-label">Opens</label>
              <input type="date" className="awards-date-input" value={data.nominations_start} onChange={(e) => setRoundField('nominations_start', e.target.value)} />
            </div>
            <div className="awards-date-row">
              <label className="awards-date-label">Closes</label>
              <input type="date" className="awards-date-input" value={data.nominations_end} onChange={(e) => setRoundField('nominations_end', e.target.value)} />
            </div>
          </div>
          <div className="awards-settings-col">
            <div className="awards-col-head">
              <span className="awards-col-label">Voting</span>
              <button
                className={`admin-toggle ${data.voting_enabled ? 'on' : 'off'}`}
                onClick={() => setRoundField('voting_enabled', !data.voting_enabled)}
              >
                <i className={`bi ${data.voting_enabled ? 'bi-toggle-on' : 'bi-toggle-off'}`} />
              </button>
            </div>
            <div className="awards-date-row">
              <label className="awards-date-label">Opens</label>
              <input type="date" className="awards-date-input" value={data.voting_start} onChange={(e) => setRoundField('voting_start', e.target.value)} />
            </div>
            <div className="awards-date-row">
              <label className="awards-date-label">Closes</label>
              <input type="date" className="awards-date-input" value={data.voting_end} onChange={(e) => setRoundField('voting_end', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="awards-form-actions">
          <button className="awards-save-btn" onClick={saveRound} disabled={roundSaving}>
            {roundSaving ? 'Saving…' : roundForm.mode === 'new' ? 'Create Round' : 'Save Changes'}
          </button>
          <button className="awards-cancel-btn" onClick={() => setRoundForm(null)}>Cancel</button>
        </div>
      </div>
    );
  };

  // ── Category form ──────────────────────────────────────────
  const renderCatForm = (roundId) => {
    if (!catForm || catForm.roundId !== roundId) return null;
    const { data } = catForm;
    return (
      <div className="awards-cat-form">
        <div className="awards-cat-form-title">
          {catForm.mode === 'new' ? 'Add Category' : 'Edit Category'}
        </div>
        <div className="awards-cat-form-grid">
          <div className="awards-form-group">
            <label className="awards-form-label">Key (slug)</label>
            <input
              className="awards-form-input"
              type="text"
              value={data.category_key}
              onChange={(e) => setCatField('category_key', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              placeholder="e.g. bridge_builder"
              disabled={catForm.mode === 'edit'}
            />
          </div>
          <div className="awards-form-group">
            <label className="awards-form-label">Label</label>
            <input
              className="awards-form-input"
              type="text"
              value={data.label}
              onChange={(e) => setCatField('label', e.target.value)}
              placeholder="e.g. Bridge Builder"
            />
          </div>
          <div className="awards-form-group awards-form-group--span2">
            <label className="awards-form-label">
              Description <span className="awards-form-opt">(optional)</span>
            </label>
            <input
              className="awards-form-input"
              type="text"
              value={data.description}
              onChange={(e) => setCatField('description', e.target.value)}
              placeholder="Brief description shown to users"
            />
          </div>
          <div className="awards-form-group">
            <label className="awards-form-label">Min Years</label>
            <input
              className="awards-form-input"
              type="number"
              value={data.min_years}
              onChange={(e) => setCatField('min_years', e.target.value)}
              min="0"
              placeholder="—"
            />
          </div>
          <div className="awards-form-group">
            <label className="awards-form-label">Max Years</label>
            <input
              className="awards-form-input"
              type="number"
              value={data.max_years}
              onChange={(e) => setCatField('max_years', e.target.value)}
              min="0"
              placeholder="—"
            />
          </div>
          <div className="awards-form-group">
            <label className="awards-form-label">Sort Order</label>
            <input
              className="awards-form-input"
              type="number"
              value={data.sort_order}
              onChange={(e) => setCatField('sort_order', parseInt(e.target.value) || 0)}
              min="0"
            />
          </div>
          <div className="awards-form-group awards-form-group--center">
            <label className="awards-form-label">Active</label>
            <button
              className={`admin-toggle ${data.active ? 'on' : 'off'}`}
              onClick={() => setCatField('active', !data.active)}
            >
              <i className={`bi ${data.active ? 'bi-toggle-on' : 'bi-toggle-off'}`} />
            </button>
          </div>
        </div>
        <div className="awards-form-actions">
          <button className="awards-save-btn awards-save-btn--sm" onClick={saveCategory} disabled={catSaving}>
            {catSaving ? 'Saving…' : catForm.mode === 'new' ? 'Add' : 'Update'}
          </button>
          <button className="awards-cancel-btn" onClick={() => setCatForm(null)}>Cancel</button>
        </div>
      </div>
    );
  };

  // ── Rounds tab ─────────────────────────────────────────────
  const renderRoundsTab = () => (
    <div>
      <div className="awards-rounds-header">
        {roundMsg && (
          <span className={`awards-settings-msg ${roundMsg.ok ? 'ok' : 'err'}`}>
            {roundMsg.text}
          </span>
        )}
        {!roundForm && (
          <button className="awards-new-round-btn" onClick={openNewRound}>
            <i className="bi bi-plus-circle me-1" />New Round
          </button>
        )}
      </div>

      {renderRoundForm()}

      {roundsLoading ? (
        <div className="admin-loading">Loading rounds…</div>
      ) : rounds.length === 0 ? (
        <div className="admin-empty">No rounds yet. Create one to get started.</div>
      ) : (
        rounds.map((round) => (
          <div key={round.round_id} className={`awards-round-card ${round.active ? 'awards-round-card--active' : ''}`}>
            <div className="awards-round-card-head">
              <div className="awards-round-card-info">
                <div className="awards-round-card-name">
                  {round.round_name}
                  <span className="awards-round-year">{round.year}</span>
                  {round.active && <span className="awards-active-badge">Active</span>}
                </div>
                <div className="awards-round-card-periods">
                  <div className="awards-round-period">
                    <span className="awards-period-label">Nominations</span>
                    <button
                      className={`admin-toggle awards-period-toggle ${round.nominations_enabled ? 'on' : 'off'}`}
                      onClick={() => toggleRoundFlag(round, 'nominations_enabled')}
                      title={round.nominations_enabled ? 'Disable nominations' : 'Enable nominations'}
                    >
                      <i className={`bi ${round.nominations_enabled ? 'bi-toggle-on' : 'bi-toggle-off'}`} />
                    </button>
                    <span className="awards-period-dates">
                      {round.nominations_start || round.nominations_end
                        ? `${formatDate(round.nominations_start)} – ${formatDate(round.nominations_end)}`
                        : <span className="awards-period-unset">No dates set</span>}
                    </span>
                  </div>
                  <div className="awards-round-period">
                    <span className="awards-period-label">Voting</span>
                    <button
                      className={`admin-toggle awards-period-toggle ${round.voting_enabled ? 'on' : 'off'}`}
                      onClick={() => toggleRoundFlag(round, 'voting_enabled')}
                      title={round.voting_enabled ? 'Disable voting' : 'Enable voting'}
                    >
                      <i className={`bi ${round.voting_enabled ? 'bi-toggle-on' : 'bi-toggle-off'}`} />
                    </button>
                    <span className="awards-period-dates">
                      {round.voting_start || round.voting_end
                        ? `${formatDate(round.voting_start)} – ${formatDate(round.voting_end)}`
                        : <span className="awards-period-unset">No dates set</span>}
                    </span>
                  </div>
                </div>
              </div>
              <div className="awards-round-card-actions">
                {!round.active && (
                  <button className="awards-btn awards-btn--activate" onClick={() => activateRound(round.round_id)}>
                    Activate
                  </button>
                )}
                <button className="awards-btn awards-btn--edit" onClick={() => openEditRound(round)}>
                  <i className="bi bi-pencil me-1" />Edit
                </button>
                <button
                  className={`awards-btn awards-btn--categories ${expandedRound === round.round_id ? 'open' : ''}`}
                  onClick={() => toggleExpandRound(round.round_id)}
                >
                  <i className="bi bi-tags me-1" />
                  Categories
                  <i className={`bi bi-chevron-${expandedRound === round.round_id ? 'up' : 'down'} ms-1`} />
                </button>
                {!round.active && (
                  <button className="awards-btn awards-btn--delete" onClick={() => deleteRound(round.round_id)} title="Delete round">
                    <i className="bi bi-trash" />
                  </button>
                )}
              </div>
            </div>

            {expandedRound === round.round_id && (
              <div className="awards-categories-panel">
                {catsLoading[round.round_id] ? (
                  <div className="admin-loading">Loading…</div>
                ) : (
                  <>
                    <div className="admin-table-wrapper">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th style={{ width: 36 }}>#</th>
                            <th>Key</th>
                            <th>Label</th>
                            <th>Description</th>
                            <th className="admin-th-center">Years</th>
                            <th className="admin-th-center">Active</th>
                            <th style={{ width: 90 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(roundCategories[round.round_id] || []).length === 0 ? (
                            <tr>
                              <td colSpan={7} className="admin-empty">No categories yet</td>
                            </tr>
                          ) : (roundCategories[round.round_id] || []).map((cat) => (
                            <tr key={cat.category_id}>
                              <td className="admin-email">{cat.sort_order}</td>
                              <td><code className="awards-cat-key">{cat.category_key}</code></td>
                              <td className="admin-name">{cat.label}</td>
                              <td className="admin-email">{cat.description || '—'}</td>
                              <td className="admin-td-center admin-email">
                                {cat.min_years != null || cat.max_years != null
                                  ? `${cat.min_years ?? ''}–${cat.max_years ?? ''} yrs`
                                  : '—'}
                              </td>
                              <td className="admin-td-center">
                                {cat.active
                                  ? <i className="bi bi-check-circle-fill" style={{ color: '#2a7a56' }} />
                                  : <i className="bi bi-x-circle" style={{ color: '#c5d4db' }} />}
                              </td>
                              <td>
                                <div className="awards-cat-row-actions">
                                  <button className="awards-btn awards-btn--sm-edit" onClick={() => openEditCategory(round.round_id, cat)}>
                                    Edit
                                  </button>
                                  <button className="awards-btn awards-btn--sm-delete" onClick={() => deleteCategory(round.round_id, cat.category_id)}>
                                    <i className="bi bi-trash" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {renderCatForm(round.round_id)}
                    {(!catForm || catForm.roundId !== round.round_id) && (
                      <div className="d-flex align-items-center gap-3">
                        <button className="awards-add-cat-btn" onClick={() => openNewCategory(round.round_id)}>
                          <i className="bi bi-plus-circle me-1" />Add New Category
                        </button>
                        {(roundCategories[round.round_id] || []).length === 0 && rounds.length > 1 && (
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">or</span>
                            <select 
                              className="awards-filter-select py-0" 
                              style={{ height: '24px', fontSize: '0.72rem' }}
                              onChange={(e) => e.target.value && handleCloneCategories(round.round_id, e.target.value)}
                              defaultValue=""
                            >
                              <option value="" disabled>Import categories from...</option>
                              {rounds.filter(r => r.round_id !== round.round_id).map(r => (
                                <option key={r.round_id} value={r.round_id}>{r.round_name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  // ── Nominations tab ────────────────────────────────────────
  const renderNominationsTab = () => (
    <>
      <div className="awards-filters">
        <select
          className="awards-filter-select"
          value={nomFilter.round_id}
          onChange={(e) => setNomFilter((f) => ({ ...f, round_id: e.target.value, category: '' }))}
        >
          <option value="">All rounds</option>
          {rounds.map((r) => (
            <option key={r.round_id} value={r.round_id}>
              {r.round_name}{r.active ? ' (Active)' : ''}
            </option>
          ))}
        </select>
        <select
          className="awards-filter-select"
          value={nomFilter.status}
          onChange={(e) => setNomFilter((f) => ({ ...f, status: e.target.value }))}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="accepted">Accepted</option>
          <option value="declined">Declined</option>
        </select>
        <select
          className="awards-filter-select"
          value={nomFilter.category}
          onChange={(e) => setNomFilter((f) => ({ ...f, category: e.target.value }))}
        >
          <option value="">All categories</option>
          {nomCategories.map((c) => (
            <option key={c.category_key} value={c.category_key}>{c.label}</option>
          ))}
        </select>
      </div>
      {nomLoading ? (
        <div className="admin-loading">Loading nominations…</div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Nominee</th>
                <th>Category</th>
                <th>Nominated By</th>
                <th className="admin-th-center">Status</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {nominations.length === 0 ? (
                <tr><td colSpan={5} className="admin-empty">No nominations found</td></tr>
              ) : nominations.map((n) => (
                <tr key={n.nomination_id}>
                  <td>
                    <div className="admin-name">{n.nominee_name}</div>
                    <div className="admin-email">{n.nominee_email}</div>
                  </td>
                  <td>
                    <span className="awards-cat-badge">
                      {nomCategories.find((c) => c.category_key === n.category)?.label || n.category}
                    </span>
                  </td>
                  <td>
                    {n.Nominator ? (
                      <>
                        <div className="admin-name">{n.Nominator.first_name} {n.Nominator.last_name}</div>
                        <div className="admin-email">{n.Nominator.email}</div>
                      </>
                    ) : '—'}
                  </td>
                  <td className="admin-td-center">
                    <span className={`awards-status-badge awards-status-${n.status}`}>{n.status}</span>
                  </td>
                  <td className="admin-email">{formatDate(n.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  // ── Standings tab ──────────────────────────────────────────
  const renderStandingsTab = () => {
    const standings = buildStandings();
    return (
      <>
        <div className="awards-filters">
          <select
            className="awards-filter-select"
            value={standingsRoundId}
            onChange={(e) => setStandingsRoundId(e.target.value)}
          >
            <option value="">Select a round</option>
            {rounds.map((r) => (
              <option key={r.round_id} value={r.round_id}>
                {r.round_name}{r.active ? ' (Active)' : ''}
              </option>
            ))}
          </select>
        </div>
        {!standingsRoundId ? (
          <div className="admin-empty">Select a round to view standings.</div>
        ) : votesLoading ? (
          <div className="admin-loading">Loading standings…</div>
        ) : (
          <div className="awards-standings-grid">
            {standingsCategories.length === 0 ? (
              <div className="admin-empty">No categories in this round.</div>
            ) : standingsCategories.map((cat) => {
              const nominees = standings[cat.category_key] || [];
              return (
                <div key={cat.category_id} className="awards-standings-card">
                  <div className="awards-standings-cat">{cat.label}</div>
                  {nominees.length === 0 ? (
                    <div className="awards-standings-empty">No accepted nominees yet</div>
                  ) : nominees.map((n, i) => (
                    <div key={n.email} className="awards-standings-row">
                      <span className={`awards-standings-rank${i === 0 ? ' first' : ''}`}>{i + 1}</span>
                      <span className="awards-standings-name">{n.name}</span>
                      <span className="awards-standings-votes">{n.votes} {n.votes === 1 ? 'vote' : 'votes'}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="awards-admin">
      <div className="awards-subtabs">
        <button
          className={`awards-subtab ${view === 'rounds' ? 'active' : ''}`}
          onClick={() => setView('rounds')}
        >
          <i className="bi bi-trophy me-1" />Rounds
        </button>
        <button
          className={`awards-subtab ${view === 'nominations' ? 'active' : ''}`}
          onClick={() => setView('nominations')}
        >
          Nominations
          {nominations.length > 0 && (
            <span className="awards-subtab-count">{nominations.length}</span>
          )}
        </button>
        <button
          className={`awards-subtab ${view === 'standings' ? 'active' : ''}`}
          onClick={() => setView('standings')}
        >
          Standings
        </button>
      </div>

      {view === 'rounds' && renderRoundsTab()}
      {view === 'nominations' && renderNominationsTab()}
      {view === 'standings' && renderStandingsTab()}
    </div>
  );
}
